/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, memo, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Space, Typography } from 'antd';
import NodeWrapper from '../../NodeWrapper';
import { GraphContext } from '../../FlowGraph';
import { GRAPH_LAYOUTS, NODE_TYPES_PROPERTIES } from '../../../../constants/flowGraph';
import LimitedConnectionsHandle from '../../LimitedConnectionsHandle';

// Misc imports
import { truncate } from '../../../../utils/string/truncate';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';

// Constant declarations
const { Text } = Typography;

const SwitchCaseNode = ({ id, type, data, dragging, selected }) => {
  const { t } = useTranslation();
  const { currentBotLanguage } = useBotLanguage();
  const { graphDirection } = useContext(GraphContext);

  const { switchCaseData } = data || {};
  const { condition, value } = switchCaseData || {};

  const nodeProps = NODE_TYPES_PROPERTIES[type];

  const NodeContents = useMemo(() => {
    return (
      <Fragment>
        <LimitedConnectionsHandle type="target" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'top' : 'left'}/>

        <Space size={6} className="node-header">
          {nodeProps.icon}
          {nodeProps.label}
        </Space>

        {
          condition && value && value[currentBotLanguage] ? (
            <Text type="secondary">
              {truncate(`${t(condition, { context: 'switch case condition' })} ${value[currentBotLanguage]}`, 30, '...')}
            </Text>
          ) : (
            <Text type="secondary">
              {t('Click to edit condition')}
            </Text>
          )
        }

        <LimitedConnectionsHandle type="source" position={graphDirection === GRAPH_LAYOUTS.VERTICAL ? 'bottom' : 'right'}/>
      </Fragment>
    );
  }, [data, nodeProps, graphDirection, currentBotLanguage]);

  return (
    <NodeWrapper
      id={id}
      type={type}
      data={data}
      children={NodeContents}
      dragging={dragging}
      selected={selected}
    />
  );
};

// https://reactflow.dev/docs/api/nodes/node-options/
SwitchCaseNode.propTypes = {
  data: PropTypes.object,
  id: PropTypes.string,
  dragging: PropTypes.bool,
  selected: PropTypes.bool,
  type: PropTypes.string,
  xPos: PropTypes.number,
  yPos: PropTypes.number
};

export default memo(SwitchCaseNode);
